import React, { useContext } from "react";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react";
import { withRouter } from "react-router-dom";
import { RouteComponentProps } from "react-router";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { UI } from "@wwimmo/ui";
import { IEcConfirmationMail } from "src/stores/CommunicationSettingsStore";
import styles from "src/screens/communicationSettings/CommunicationSettings.module.css";

const EasyContactCommunicationSettingsBase = (props: RouteComponentProps) => {
    const { t } = useTranslation();
    const { communicationSettingsStore, navStore } = useContext(RootStoreContext);

    const onChangeConfirmationMail =
        (language: keyof IEcConfirmationMail) => (e: React.ChangeEvent<HTMLInputElement>) => {
            communicationSettingsStore.updateEcConfirmationMail(language, e.target.value);
        };

    return (
        <UI.Row>
            <UI.Col md={12}>
                <UI.Card
                    title={t("screens.communication_settings.easycontact_ticketing.easycontact.title").toString()}
                    className={styles.SettingsContainer}
                >
                    {navStore.hasFeatureTicketing && (
                        <React.Fragment>
                            <UI.Row className="mb-3">
                                <UI.Col>
                                    <h6 className="bold-text" style={{ color: "var(--primary)" }}>
                                        {t(
                                            "screens.communication_settings.easycontact_ticketing.easycontact.mail_inbox.subtitle"
                                        )}
                                    </h6>
                                </UI.Col>
                            </UI.Row>
                            <UI.Row className="mb-3">
                                <UI.Col>
                                    <div className="d-flex align-items-center">
                                        <UI.Switch
                                            checked={communicationSettingsStore.sendEmailEcAndTicketing}
                                            onToggle={communicationSettingsStore.toggleSendEmailEcAndTicketing}
                                        />
                                        <span className="inline-block ml-2">
                                            {t(
                                                "screens.communication_settings.easycontact_ticketing.easycontact.mail_inbox.send_email_switch"
                                            )}
                                        </span>
                                    </div>
                                </UI.Col>
                            </UI.Row>
                            <hr />
                        </React.Fragment>
                    )}
                    <UI.Row className="mb-3">
                        <UI.Col>
                            <h6 className="bold-text" style={{ color: "var(--primary)" }}>
                                {t(
                                    "screens.communication_settings.easycontact_ticketing.easycontact.fallback_email.subtitle"
                                )}
                            </h6>
                            <span>
                                {t(
                                    "screens.communication_settings.easycontact_ticketing.easycontact.fallback_email.explanation_text"
                                )}
                            </span>
                        </UI.Col>
                    </UI.Row>
                    <UI.Row className="align-items-center mb-3">
                        <UI.Col md={4} lg={3}>
                            <label className="m-0 bold-text" style={{ color: "var(--primary)" }}>
                                {t(
                                    "screens.communication_settings.easycontact_ticketing.easycontact.fallback_email.email_address"
                                )}
                            </label>
                        </UI.Col>
                        <UI.Col md={8} lg={6} className={styles.InputCol}>
                            <UI.Input
                                type="text"
                                value={communicationSettingsStore.ecFallbackEmail}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    communicationSettingsStore.updateEcFallbackEmail(e.target.value)
                                }
                                errorMsg={communicationSettingsStore.ecFallbackEmailError}
                            />
                        </UI.Col>
                    </UI.Row>
                    <hr />
                    <UI.Row className="mb-3">
                        <UI.Col>
                            <h6 className="bold-text" style={{ color: "var(--primary)" }}>
                                {t(
                                    "screens.communication_settings.easycontact_ticketing.easycontact.confirmation_mail.subtitle"
                                )}
                            </h6>
                            <span>
                                {t(
                                    "screens.communication_settings.easycontact_ticketing.easycontact.confirmation_mail.explanation_text"
                                )}
                            </span>
                        </UI.Col>
                    </UI.Row>
                    <UI.Row className="mb-3">
                        <UI.Col>
                            {navStore.hasOptionMultilingual ? (
                                <UI.BootstrapTabs
                                    defaultActiveKey="german"
                                    id="ec-confirmation-text-language"
                                    className="mb-3"
                                >
                                    <UI.Tab eventKey="german" title="Deutsch">
                                        <UI.Input
                                            as="textarea"
                                            type="text"
                                            value={communicationSettingsStore.ecConfirmationMail.de}
                                            onChange={onChangeConfirmationMail("de")}
                                            className={styles.ConfirmationEmailTextArea}
                                        />
                                    </UI.Tab>
                                    <UI.Tab eventKey="french" title="Französisch">
                                        <UI.Input
                                            as="textarea"
                                            type="text"
                                            value={communicationSettingsStore.ecConfirmationMail.fr}
                                            onChange={onChangeConfirmationMail("fr")}
                                            className={styles.ConfirmationEmailTextArea}
                                        />
                                    </UI.Tab>
                                    <UI.Tab eventKey="italian" title="Italienisch">
                                        <UI.Input
                                            as="textarea"
                                            type="text"
                                            value={communicationSettingsStore.ecConfirmationMail.it}
                                            onChange={onChangeConfirmationMail("it")}
                                            className={styles.ConfirmationEmailTextArea}
                                        />
                                    </UI.Tab>
                                    <UI.Tab eventKey="english" title="Englisch">
                                        <UI.Input
                                            as="textarea"
                                            type="text"
                                            value={communicationSettingsStore.ecConfirmationMail.en}
                                            onChange={onChangeConfirmationMail("en")}
                                            className={styles.ConfirmationEmailTextArea}
                                        />
                                    </UI.Tab>
                                </UI.BootstrapTabs>
                            ) : (
                                <UI.Input
                                    as="textarea"
                                    type="text"
                                    value={communicationSettingsStore.ecConfirmationMail.de}
                                    onChange={onChangeConfirmationMail("de")}
                                    className={styles.ConfirmationEmailTextArea}
                                />
                            )}
                        </UI.Col>
                    </UI.Row>
                </UI.Card>
            </UI.Col>
        </UI.Row>
    );
};

export const EasyContactCommunicationSettings = withRouter(
    Sentry.withProfiler(observer(EasyContactCommunicationSettingsBase))
);
